import { MixedCardItem } from '../../Teams/DataboxItems'
import {
  buildDataboxODataFilter,
  extractCognitiveSearchIdsFromOData,
  extractDataboxIdsFromOData
} from './helpers'
import { MCP_SERVER_NAMES } from './constants'

export interface OriginalDataSourceIds {
  dataBoxIds: string[]
  dataProductIds: string[]
}

export type OriginalDataSourcesByMcp = Record<string, OriginalDataSourceIds>

export interface ExtractedDataSources {
  dataSourcesByMcp: OriginalDataSourcesByMcp | null
  allDataBoxIds: string[]
  allDataProductIds: string[]
  dataProductsByDatabox: Array<{ dataProductId: string; databoxId: string }>
  personalDataboxResourceIds: string[]
  personalDataboxMcpServerId: string | null
  otherMCPs: string[]
}

export interface ParsedMcpServerSources {
  databoxIds: string[]
  dataProductIds: string[]
  teamIdentifiers: Record<string, string>
  cognitiveSearchIds: string[]
  dataProductsByDatabox: Array<{ dataProductId: string; databoxId: string }>
  smartInsightsTeamIdentifier?: string
  webSearchId?: string
}

export interface ComparableMcpScope {
  mcpServerId: string
  filter: string
  groups: Array<{ databoxId: string | null; dataProductIds: string[] }>
  dataProductIds: string[]
  fullySelectedDataboxIds: string[]
}

export const parseScopeConfiguration = (scopeConfiguration: unknown): any | null => {
  if (!scopeConfiguration) {
    return null
  }

  try {
    return typeof scopeConfiguration === 'string'
      ? JSON.parse(scopeConfiguration)
      : scopeConfiguration
  } catch (error) {
    console.error('Error parsing scopeConfiguration:', error)
    return null
  }
}

export const extractOriginalDataSourceIds = (
  agentMcpServers: any[] | null
): ExtractedDataSources => {
  if (!agentMcpServers || agentMcpServers.length === 0) {
    return {
      dataSourcesByMcp: null,
      allDataBoxIds: [],
      allDataProductIds: [],
      dataProductsByDatabox: [],
      personalDataboxResourceIds: [],
      personalDataboxMcpServerId: null,
      otherMCPs: []
    }
  }

  const dataSourcesByMcp: OriginalDataSourcesByMcp = {}
  const allDataBoxIdsSet = new Set<string>()
  const allDataProductIdsSet = new Set<string>()
  const dataProductsByDataboxArray: Array<{ dataProductId: string; databoxId: string }> = []
  const personalDataboxResourceIds: string[] = []
  const otherMCPs: string[] = []
  let personalDataboxMcpServerId: string | null = null

  agentMcpServers.forEach((server: any) => {
    const mcpServerId = server.mcpServerId
    if (!mcpServerId) {
      return
    }

    let dataBoxIds: string[] = []
    const dataProductIds: string[] = []
    const parsedScopeConfig = parseScopeConfiguration(server.scopeConfiguration)

    if (server.type === 'PersonalDatabox' || parsedScopeConfig?.type === 'PersonalDatabox') {
      personalDataboxMcpServerId = mcpServerId
      ;(parsedScopeConfig?.resourceIds || []).forEach((id: any) => {
        const stringId = String(id)
        if (stringId && stringId !== 'undefined' && stringId !== 'null') {
          personalDataboxResourceIds.push(stringId)
        }
      })
      return
    }

    if (server.scopeConfiguration) {
      if (!parsedScopeConfig) {
        return
      }

      dataBoxIds = extractDataboxIdsFromOData(parsedScopeConfig.filters || '')

      const dataProductGroups = parsedScopeConfig.customSelection?.dataProducts || []

      dataProductGroups.forEach((group: any) => {
        const groupProductIds = (group.dataProductIds || [])
          .map((id: any) => String(id))
          .filter((id: string) => id && id !== 'undefined' && id !== 'null')

        dataProductIds.push(...groupProductIds)

        if (group.databoxId === null || group.databoxId === undefined) {
          groupProductIds.forEach((id: string) => allDataProductIdsSet.add(id))
        } else {
          const databoxId = String(group.databoxId)
          groupProductIds.forEach((id: string) => {
            dataProductsByDataboxArray.push({ dataProductId: id, databoxId })
          })
          allDataBoxIdsSet.add(databoxId)
        }
      })

      dataSourcesByMcp[mcpServerId] = {
        dataBoxIds,
        dataProductIds
      }
    } else {
      otherMCPs.push(mcpServerId)
    }

    dataBoxIds.forEach((id: string) => allDataBoxIdsSet.add(id))
  })

  return {
    dataSourcesByMcp,
    allDataBoxIds: Array.from(allDataBoxIdsSet),
    allDataProductIds: Array.from(allDataProductIdsSet),
    dataProductsByDatabox: dataProductsByDataboxArray,
    personalDataboxResourceIds: Array.from(new Set(personalDataboxResourceIds)),
    personalDataboxMcpServerId,
    otherMCPs: Array.from(new Set(otherMCPs))
  }
}

export const parseMcpServerSources = (
  mcpServersArgs: any[] | null | undefined,
  availableMcpServers: any[] | null | undefined
): ParsedMcpServerSources => {
  if (!mcpServersArgs || !Array.isArray(mcpServersArgs) || mcpServersArgs.length === 0) {
    return {
      databoxIds: [],
      dataProductIds: [],
      teamIdentifiers: {},
      cognitiveSearchIds: [],
      dataProductsByDatabox: [],
      smartInsightsTeamIdentifier: undefined,
      webSearchId: undefined
    }
  }

  const { allDataBoxIds, allDataProductIds, dataProductsByDatabox, otherMCPs } =
    extractOriginalDataSourceIds(mcpServersArgs)

  const webSearchMcp =
    availableMcpServers?.find((server: any) => server?.name === MCP_SERVER_NAMES.WEB_SEARCH) || null
  const webSearchId = otherMCPs.find((mcpId: string) => mcpId === webSearchMcp?.id) || undefined

  const teamIdentifiersMap: Record<string, string> = {}
  const allCognitiveSearchIds: string[] = []
  let smartInsightsTeamIdentifier: string | undefined

  mcpServersArgs.forEach((mcpServer: any) => {
    const config = parseScopeConfiguration(mcpServer.scopeConfiguration)
    if (!config) {
      return
    }

    if (config.teamIdentifier) {
      smartInsightsTeamIdentifier = smartInsightsTeamIdentifier || config.teamIdentifier
      const databoxIdsForServer = config.filters ? extractDataboxIdsFromOData(config.filters) : []
      databoxIdsForServer.forEach((id: string) => {
        teamIdentifiersMap[String(id)] = config.teamIdentifier
      })
    }

    if (config.filters) {
      allCognitiveSearchIds.push(...extractCognitiveSearchIdsFromOData(config.filters))
    }
  })

  return {
    databoxIds: allDataBoxIds,
    dataProductIds: allDataProductIds,
    teamIdentifiers: teamIdentifiersMap,
    cognitiveSearchIds: [...new Set(allCognitiveSearchIds)],
    dataProductsByDatabox,
    smartInsightsTeamIdentifier,
    webSearchId
  }
}

export const buildPersonalDataBoxesPayload = (
  items: Array<
    Pick<MixedCardItem, 'id' | 'smartDocsDataboxId' | 'type'> & {
      scope?: string
      selectedItems?: any[]
    }
  >
): Array<{ personalDataBoxId: string; resourceIds: string[] }> | undefined => {
  const personalDatabox = items.find(
    (item: any) => item.scope === 'Personal'
  )
  const resourceIds = (personalDatabox?.selectedItems || [])
    .filter((item: any) => item.type === 'file')
    .map((item: any) => String(item.id))
    .filter((id: string) => id && id !== 'undefined' && id !== 'null')

  if (resourceIds.length === 0) {
    return undefined
  }

  return [
    {
      personalDataBoxId: String(personalDatabox?.smartDocsDataboxId || personalDatabox?.id),
      resourceIds
    }
  ]
}

export const findFirstMcpTeamIdentifier = (
  mcpServers: any[] | null | undefined
): string | undefined => {
  if (!mcpServers) {
    return undefined
  }

  for (const mcpServer of mcpServers) {
    const config = parseScopeConfiguration(mcpServer?.scopeConfiguration)
    if (config?.teamIdentifier) {
      return config.teamIdentifier
    }
  }

  return undefined
}

export const findMcpScopeConfiguration = (
  mcpServers: any[] | null | undefined,
  mcpServerId?: string
): any | null => {
  if (!mcpServerId || !mcpServers) {
    return null
  }

  const entry = mcpServers.find((mcpServer: any) => mcpServer.mcpServerId === mcpServerId)
  return parseScopeConfiguration(entry?.scopeConfiguration)
}

export const getTeamScopedDataboxes = <T extends { id: string | number }>(items: T[] = []): T[] => {
  return items.filter((item) => /^\d+$/.test(String(item.id)))
}

export const hasFileLevelDataboxSelection = (
  selectedDataboxes: Array<{ selectedItems?: any[] }> = []
): boolean => {
  return selectedDataboxes.some((db: any) =>
    db.selectedItems?.some((item: any) => item.type === 'file')
  )
}

export const isDataboxSelectionUnchanged = ({
  originalFilter,
  currentDataboxIds,
  selectedDataboxes
}: {
  originalFilter?: string
  currentDataboxIds: string[]
  selectedDataboxes: Array<{ id: string | number; selectedItems?: any[] }>
}): boolean => {
  const originalDataboxIds = originalFilter ? extractDataboxIdsFromOData(originalFilter) : []
  const hasFileLevelEdits = hasFileLevelDataboxSelection(selectedDataboxes)

  return (
    !hasFileLevelEdits &&
    originalDataboxIds.length === currentDataboxIds.length &&
    originalDataboxIds.every((id: string) => currentDataboxIds.includes(id))
  )
}

export const resolveSmartInsightsFilter = ({
  originalFilter,
  currentDataboxIds,
  selectedDataboxes
}: {
  originalFilter?: string
  currentDataboxIds: string[]
  selectedDataboxes: Array<{ id: string | number; selectedItems?: any[] }>
}): string => {
  const selectionUnchanged = isDataboxSelectionUnchanged({
    originalFilter,
    currentDataboxIds,
    selectedDataboxes
  })

  return selectionUnchanged && originalFilter
    ? originalFilter
    : buildDataboxODataFilter(
        getTeamScopedDataboxes(selectedDataboxes).map((databox) => ({
          ...databox,
          id: String(databox.id)
        }))
      )
}

export const buildSmartInsightsScopeConfiguration = ({
  originalScopeConfiguration,
  currentDataboxIds,
  selectedDataboxes,
  teamIdentifier,
  trackingId,
  preserveOtherConfig = true
}: {
  originalScopeConfiguration?: any | null
  currentDataboxIds: string[]
  selectedDataboxes: Array<{ id: string | number; selectedItems?: any[] }>
  teamIdentifier?: string
  trackingId?: string | null
  preserveOtherConfig?: boolean
}) => {
  const { dataBoxIds, dataProductIds, filters, customSelection, ...otherConfig } =
    originalScopeConfiguration || {}

  return {
    ...(preserveOtherConfig ? otherConfig : {}),
    filters: resolveSmartInsightsFilter({
      originalFilter: filters,
      currentDataboxIds,
      selectedDataboxes
    }),
    ...(teamIdentifier && { teamIdentifier }),
    ...(trackingId !== undefined && { trackingId })
  }
}

export const normalizeComparableMcpScope = ({
  mcpServerId,
  filter,
  groups,
  dataProductScopes,
  fullySelectedDataboxIds
}: {
  mcpServerId: string
  filter?: string
  groups?: any[]
  dataProductScopes?: any[]
  fullySelectedDataboxIds?: string[]
}): ComparableMcpScope => ({
  mcpServerId,
  filter: filter || '',
  groups: (groups || [])
    .map((group: any) => ({
      databoxId: group.databoxId ?? null,
      dataProductIds: [...(group.dataProductIds || [])].map(String).sort()
    }))
    .sort((a, b) => String(a.databoxId).localeCompare(String(b.databoxId))),
  dataProductIds: (dataProductScopes || []).map((scope: any) => String(scope.id)).sort(),
  fullySelectedDataboxIds: [...(fullySelectedDataboxIds || [])].map(String).sort()
})

export const serializeComparableMcpScopes = (scopes: ComparableMcpScope[]): string => {
  return JSON.stringify([...scopes].sort((a, b) => a.mcpServerId.localeCompare(b.mcpServerId)))
}

export const normalizeDraftMcpServerScope = (server: any): ComparableMcpScope => {
  return normalizeComparableMcpScope({
    mcpServerId: server.mcpServerId,
    filter: server.filter,
    groups: server.dataProductGroups,
    dataProductScopes: server.dataProductScopes,
    fullySelectedDataboxIds: server.fullySelectedDataboxIds
  })
}

export const normalizeSavedMcpServerScope = (server: any): ComparableMcpScope => {
  const config = parseScopeConfiguration(server?.scopeConfiguration) || {}
  return normalizeComparableMcpScope({
    mcpServerId: server.mcpServerId,
    filter: config.filters,
    groups: config.customSelection?.dataProducts,
    dataProductScopes: config.dataProductScopes,
    fullySelectedDataboxIds: config.fullySelectedDataboxIds
  })
}

export const haveComparableMcpScopesChanged = (
  draftServers: any[],
  savedServers: any[]
): boolean => {
  const currentScopes = serializeComparableMcpScopes(
    draftServers.map((server: any) => normalizeDraftMcpServerScope(server))
  )
  const savedScopes = serializeComparableMcpScopes(
    savedServers.map((server: any) => normalizeSavedMcpServerScope(server))
  )

  return currentScopes !== savedScopes
}

const getMcpServerId = (server: any): string => String(server?.mcpServerId || server?.id || '')

const getComparableThreadMcpServer = (server: any) => ({
  isEnabled: server?.isEnabled !== false,
  scope: normalizeSavedMcpServerScope(server)
})

export const getChangedThreadMcpServers = (
  currentServers: any[] | null | undefined,
  baseServers: any[] | null | undefined
): any[] => {
  if (!Array.isArray(currentServers) || currentServers.length === 0) {
    return []
  }

  const baseServersById = new Map(
    (baseServers || [])
      .map((server: any) => [getMcpServerId(server), server] as const)
      .filter(([id]) => !!id)
  )

  return currentServers.filter((server: any) => {
    const serverId = getMcpServerId(server)
    if (!serverId) {
      return false
    }

    const baseServer = baseServersById.get(serverId)
    if (!baseServer) {
      return true
    }

    const currentComparable = getComparableThreadMcpServer(server)
    const baseComparable = getComparableThreadMcpServer(baseServer)

    return JSON.stringify(currentComparable) !== JSON.stringify(baseComparable)
  })
}

const normalizePersonalDataBox = (box: any) => ({
  personalDataBoxId: String(box?.personalDataBoxId || box?.id || ''),
  resourceIds: [
    ...new Set(
      (box?.resourceIds || [])
        .map((id: any) => String(id))
        .filter((id: string) => id && id !== 'undefined' && id !== 'null')
    )
  ].sort()
})

export const getChangedPersonalDataBoxes = (
  currentBoxes: any[] | null | undefined,
  baseBoxes: any[] | null | undefined
): any[] => {
  if (!Array.isArray(currentBoxes) || currentBoxes.length === 0) {
    return []
  }

  const baseBoxesById = new Map(
    (baseBoxes || [])
      .map((box: any) => {
        const normalized = normalizePersonalDataBox(box)
        return [normalized.personalDataBoxId, normalized] as const
      })
      .filter(([id]) => !!id)
  )

  return currentBoxes.filter((box: any) => {
    const current = normalizePersonalDataBox(box)
    if (!current.personalDataBoxId) {
      return false
    }

    const base = baseBoxesById.get(current.personalDataBoxId)
    if (!base) {
      return true
    }

    return JSON.stringify(current) !== JSON.stringify(base)
  })
}

// Frozen view of the agent-level MCP config: the same locked-id sets the DataSources modal uses
// to prevent editing agent items, plus per-bucket detail so a badge-side "current selection ==
// agent config" check can compare bucket-by-bucket. Locked items can only be added-to, never
// removed, so equality reduces to per-bucket size comparisons.
export interface AgentConfigSnapshot {
  lockedDataboxIds: Set<string>
  lockedDataProductIds: Set<string>
  lockedItemIds: Set<string>
  personalDataBoxId?: string
  personalDataboxResourceIds: Set<string>
  cognitiveSearchIds: Set<string>
  nestedDataProductsByDatabox: Map<string, Set<string>>
}

const toAgentIdSet = (ids: Array<string | null | undefined> | undefined): Set<string> =>
  new Set(
    (ids || [])
      .map((id) => (id === null ? '' : String(id)))
      .filter((id) => id && id !== 'undefined' && id !== 'null')
  )

export const buildAgentConfigSnapshot = (agentSources: {
  databoxIds?: string[]
  dataProductIds?: string[]
  cognitiveSearchIds?: string[]
  personalDataboxResourceIds?: string[]
  personalDataBoxId?: string
  dataProductsByDatabox?: Array<{ dataProductId: string; databoxId: string }>
}): AgentConfigSnapshot => {
  const cognitiveSearchIds = toAgentIdSet(agentSources.cognitiveSearchIds)
  const personalDataboxResourceIds = toAgentIdSet(agentSources.personalDataboxResourceIds)

  const nestedDataProductsByDatabox = new Map<string, Set<string>>()
  ;(agentSources.dataProductsByDatabox || []).forEach(({ databoxId, dataProductId }) => {
    const dbKey = String(databoxId)
    if (!nestedDataProductsByDatabox.has(dbKey)) {
      nestedDataProductsByDatabox.set(dbKey, new Set<string>())
    }
    nestedDataProductsByDatabox.get(dbKey)!.add(String(dataProductId))
  })

  const lockedDataboxIds = toAgentIdSet([
    ...(agentSources.databoxIds || []),
    agentSources.personalDataBoxId
  ])
  const lockedDataProductIds = toAgentIdSet(agentSources.dataProductIds)

  const lockedItemIds = new Set<string>()
  cognitiveSearchIds.forEach((id) => lockedItemIds.add(id))
  personalDataboxResourceIds.forEach((id) => lockedItemIds.add(id))
  nestedDataProductsByDatabox.forEach((set) => set.forEach((id) => lockedItemIds.add(id)))

  return {
    lockedDataboxIds,
    lockedDataProductIds,
    lockedItemIds,
    personalDataBoxId: agentSources.personalDataBoxId,
    personalDataboxResourceIds,
    cognitiveSearchIds,
    nestedDataProductsByDatabox
  }
}

// True when the live selection is exactly the agent's default MCP config. Modal invariant: the
// user cannot un-check an agent-level item, so extras are the only possible divergence and a
// per-bucket size comparison is sufficient.
export const isSelectionEqualToAgentConfig = (
  selectedDataboxes: Array<any>,
  selectedDataProducts: Array<any>,
  snapshot: AgentConfigSnapshot
): boolean => {
  if (selectedDataProducts.length !== snapshot.lockedDataProductIds.size) return false

  const personalEntry = selectedDataboxes.find((db: any) => db?.scope === 'Personal')
  const teamEntries = selectedDataboxes.filter((db: any) => db?.scope !== 'Personal')

  const expectedTeamCount = snapshot.lockedDataboxIds.size - (snapshot.personalDataBoxId ? 1 : 0)
  if (teamEntries.length !== expectedTeamCount) return false

  let totalFiles = 0
  let totalNestedDataProducts = 0
  for (const db of teamEntries) {
    for (const item of db.selectedItems || []) {
      if (item?.type === 'file') totalFiles++
      else if (item?.type === 'dataproduct') totalNestedDataProducts++
    }
  }
  if (totalFiles !== snapshot.cognitiveSearchIds.size) return false

  let expectedNestedTotal = 0
  snapshot.nestedDataProductsByDatabox.forEach((set) => {
    expectedNestedTotal += set.size
  })
  if (totalNestedDataProducts !== expectedNestedTotal) return false

  const personalFiles = (personalEntry?.selectedItems || []).filter(
    (item: any) => item?.type === 'file'
  )
  if (personalFiles.length !== snapshot.personalDataboxResourceIds.size) return false

  return true
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isPersonalDatabox = (id: unknown): id is string =>
  typeof id === 'string' && GUID_PATTERN.test(id)
